import React
from "react";


//--------------------------------------------------------
// Risk colours
//--------------------------------------------------------

const RISK_GREEN =
    "#35b978";

const RISK_AMBER =
    "#f4b942";

const RISK_ORANGE =
    "#f07b32";

const RISK_RED =
    "#df4b4b";


//--------------------------------------------------------
// Component
//--------------------------------------------------------

export default function InsightDashboardRiskMatrix({
    data = [],
    clickFunction
})
{

    //--------------------------------------------------------
    // Get total for cell
    //--------------------------------------------------------

    function getCellTotal(
        probability,
        consequence
    )
    {
        const item =
            data.find(
                (row) =>
                Number(row.probability)
                ===
                probability
                &&
                Number(row.consequence)
                ===
                consequence
            );


        return item
            ?
            Number(item.total || 0)
            :
            0;
    }


    //--------------------------------------------------------
    // Risk score
    //--------------------------------------------------------

    function getRiskScore(
        probability,
        consequence
    )
    {
        return probability
            *
            consequence;
    }


    //--------------------------------------------------------
    // Risk colour
    //--------------------------------------------------------

    function getRiskColour(
        probability,
        consequence
    )
    {
        const score =
            getRiskScore(
                probability,
                consequence
            );


        if(score >= 15)
        {
            return RISK_RED;
        }


        if(score >= 10)
        {
            return RISK_ORANGE;
        }


        if(score >= 5)
        {
            return RISK_AMBER;
        }


        return RISK_GREEN;
    }


    //--------------------------------------------------------
    // Risk description
    //--------------------------------------------------------

    function getRiskDescription(
        probability,
        consequence
    )
    {
        const score =
            getRiskScore(
                probability,
                consequence
            );


        if(score >= 15)
        {
            return "Critical";
        }


        if(score >= 10)
        {
            return "High";
        }


        if(score >= 5)
        {
            return "Medium";
        }


        return "Low";
    }


    //--------------------------------------------------------
    // Bubble size
    //--------------------------------------------------------

    function getBubbleSize(total)
    {
        if(total <= 0)
        {
            return 0;
        }


        if(total >= 50)
        {
            return 42;
        }


        if(total >= 25)
        {
            return 38;
        }


        if(total >= 10)
        {
            return 34;
        }


        if(total >= 5)
        {
            return 30;
        }


        return 26;
    }


    //--------------------------------------------------------
    // Click
    //--------------------------------------------------------

    function handleClick(
        probability,
        consequence
    )
    {
        const total =
            getCellTotal(
                probability,
                consequence
            );


        if(
            total === 0
            ||
            !clickFunction
        )
        {
            return;
        }


        clickFunction(
            probability,
            consequence
        );
    }


    //--------------------------------------------------------
    // Build rows
    //--------------------------------------------------------

    const rows =
        [
            5,
            4,
            3,
            2,
            1
        ];


    const columns =
        [
            1,
            2,
            3,
            4,
            5
        ];


    //--------------------------------------------------------
    // Render
    //--------------------------------------------------------

    return (

        <div style={containerStyle}>


            {/*--------------------------------------------*/}
            {/* MATRIX AREA                                */}
            {/*--------------------------------------------*/}

            <div style={matrixOuterStyle}>


                {/*----------------------------------------*/}
                {/* Consequence label                      */}
                {/*----------------------------------------*/}

                <div style={consequenceLabelStyle}>
                    CONSEQUENCE
                </div>


                {/*----------------------------------------*/}
                {/* Y axis                                 */}
                {/*----------------------------------------*/}

                <div style={yAxisStyle}>

                    {
                        rows.map(
                            (consequence) =>
                            {
                                return (

                                    <div
                                        key={
                                            consequence
                                        }

                                        style={
                                            axisNumberStyle
                                        }
                                    >
                                        {consequence}
                                    </div>

                                );
                            }
                        )
                    }

                </div>


                {/*----------------------------------------*/}
                {/* Grid                                   */}
                {/*----------------------------------------*/}

                <div style={gridStyle}>

                    {
                        rows.map(
                            (consequence) =>
                            {
                                return columns.map(
                                    (probability) =>
                                    {
                                        const total =
                                            getCellTotal(
                                                probability,
                                                consequence
                                            );


                                        const colour =
                                            getRiskColour(
                                                probability,
                                                consequence
                                            );


                                        const risk =
                                            getRiskDescription(
                                                probability,
                                                consequence
                                            );


                                        return (

                                            <RiskCell
                                                key={
                                                    probability
                                                    +
                                                    "-"
                                                    +
                                                    consequence
                                                }

                                                probability={
                                                    probability
                                                }

                                                consequence={
                                                    consequence
                                                }

                                                total={
                                                    total
                                                }

                                                colour={
                                                    colour
                                                }

                                                risk={
                                                    risk
                                                }

                                                bubbleSize={
                                                    getBubbleSize(
                                                        total
                                                    )
                                                }

                                                clickFunction={
                                                    () =>
                                                    {
                                                        handleClick(
                                                            probability,
                                                            consequence
                                                        );
                                                    }
                                                }
                                            />

                                        );
                                    }
                                );
                            }
                        )
                    }

                </div>


                {/*----------------------------------------*/}
                {/* X axis                                 */}
                {/*----------------------------------------*/}

                <div style={xAxisStyle}>

                    {
                        columns.map(
                            (probability) =>
                            {
                                return (

                                    <div
                                        key={
                                            probability
                                        }

                                        style={
                                            axisNumberStyle
                                        }
                                    >
                                        {probability}
                                    </div>

                                );
                            }
                        )
                    }

                </div>


                <div style={probabilityLabelStyle}>
                    PROBABILITY
                </div>

            </div>


            {/*--------------------------------------------*/}
            {/* Legend                                     */}
            {/*--------------------------------------------*/}

            <div style={legendStyle}>

                <LegendItem
                    colour={RISK_GREEN}
                    label="Low"
                />

                <LegendItem
                    colour={RISK_AMBER}
                    label="Medium"
                />

                <LegendItem
                    colour={RISK_ORANGE}
                    label="High"
                />

                <LegendItem
                    colour={RISK_RED}
                    label="Critical"
                />

            </div>

        </div>

    );
}


//--------------------------------------------------------
// Risk cell
//--------------------------------------------------------

function RiskCell({
    probability,
    consequence,
    total,
    colour,
    risk,
    bubbleSize,
    clickFunction
})
{

    const [
        hover,
        setHover
    ] =
        React.useState(
            false
        );


    const populated =
        total > 0;


    return (

        <div
            title={
                "Probability: "
                +
                probability
                +
                "\nConsequence: "
                +
                consequence
                +
                "\nRisk: "
                +
                risk
                +
                "\nInsights: "
                +
                total
            }

            onClick={
                populated
                    ?
                    clickFunction
                    :
                    undefined
            }

            onMouseEnter={
                () =>
                {
                    setHover(
                        true
                    );
                }
            }

            onMouseLeave={
                () =>
                {
                    setHover(
                        false
                    );
                }
            }

            style={{
                ...cellStyle,

                background:
                    colour,

                cursor:
                    populated
                        ?
                        "pointer"
                        :
                        "default",

                transform:
                    hover
                    &&
                    populated
                        ?
                        "scale(1.045)"
                        :
                        "scale(1)",

                zIndex:
                    hover
                    &&
                    populated
                        ?
                        2
                        :
                        1,

                boxShadow:
                    hover
                    &&
                    populated
                        ?
                        "0 7px 18px rgba(20,30,40,0.20)"
                        :
                        "inset 0 0 0 1px rgba(255,255,255,0.22)"
            }}
        >

            {
                populated
                &&
                <div
                    style={{
                        ...bubbleStyle,

                        width:
                            bubbleSize,

                        height:
                            bubbleSize
                    }}
                >

                    {total}

                </div>
            }

        </div>

    );
}


//--------------------------------------------------------
// Legend item
//--------------------------------------------------------

function LegendItem({
    colour,
    label
})
{

    return (

        <div style={legendItemStyle}>

            <div
                style={{
                    ...legendColourStyle,

                    background:
                        colour
                }}
            />

            <div>
                {label}
            </div>

        </div>

    );
}


//--------------------------------------------------------
// Container
//--------------------------------------------------------

const containerStyle =
{
    width: "100%",
    height: "100%",

    minHeight: 0,

    display: "flex",
    flexDirection: "column",

    alignItems: "center",
    justifyContent: "center"
};


//--------------------------------------------------------
// Matrix
//--------------------------------------------------------

const matrixOuterStyle =
{
    position: "relative",

    width: "100%",
    maxWidth: "310px",

    display: "grid",

    gridTemplateColumns:
        "18px 20px minmax(0, 1fr)",

    gridTemplateRows:
        "minmax(0, 1fr) 18px 16px",

    columnGap: "5px",
    rowGap: "3px"
};


const gridStyle =
{
    gridColumn: "3",
    gridRow: "1",

    aspectRatio: "1 / 1",

    display: "grid",

    gridTemplateColumns:
        "repeat(5, minmax(0, 1fr))",

    gridTemplateRows:
        "repeat(5, minmax(0, 1fr))",

    gap: "3px"
};


//--------------------------------------------------------
// Cell
//--------------------------------------------------------

const cellStyle =
{
    position: "relative",

    minWidth: 0,
    minHeight: 0,

    display: "flex",

    alignItems: "center",
    justifyContent: "center",

    borderRadius: "7px",

    overflow: "visible",

    transition:
        "transform 140ms ease, box-shadow 140ms ease"
};


//--------------------------------------------------------
// Count bubble
//--------------------------------------------------------

const bubbleStyle =
{
    display: "flex",

    alignItems: "center",
    justifyContent: "center",

    flexShrink: 0,

    borderRadius: "50%",

    /*
     * Semi-transparent dark glass bubble.
     */

    background:
        "rgba(16,20,25,0.38)",

    border:
        "1px solid rgba(255,255,255,0.42)",

    boxShadow:
        "0 3px 10px rgba(0,0,0,0.15)",

    backdropFilter:
        "blur(4px)",

    color:
        "#ffffff",

    fontSize:
        "10px",

    fontWeight:
        "700",

    fontVariantNumeric:
        "tabular-nums",

    transition:
        "width 180ms ease, height 180ms ease"
};


//--------------------------------------------------------
// Y axis
//--------------------------------------------------------

const yAxisStyle =
{
    gridColumn: "2",
    gridRow: "1",

    display: "grid",

    gridTemplateRows:
        "repeat(5, minmax(0, 1fr))"
};


//--------------------------------------------------------
// X axis
//--------------------------------------------------------

const xAxisStyle =
{
    gridColumn: "3",
    gridRow: "2",

    display: "grid",

    gridTemplateColumns:
        "repeat(5, minmax(0, 1fr))"
};


//--------------------------------------------------------
// Axis number
//--------------------------------------------------------

const axisNumberStyle =
{
    display: "flex",

    alignItems: "center",
    justifyContent: "center",

    color:
        "#8c96a4",

    fontSize:
        "8px",

    fontWeight:
        "700"
};


//--------------------------------------------------------
// Axis labels
//--------------------------------------------------------

const consequenceLabelStyle =
{
    gridColumn: "1",
    gridRow: "1",

    display: "flex",

    alignItems: "center",
    justifyContent: "center",

    writingMode:
        "vertical-rl",

    transform:
        "rotate(180deg)",

    color:
        "#a0a8b3",

    fontSize:
        "7px",

    fontWeight:
        "800",

    letterSpacing:
        "1px"
};


const probabilityLabelStyle =
{
    gridColumn: "3",
    gridRow: "3",

    display: "flex",

    alignItems: "center",
    justifyContent: "center",

    color:
        "#a0a8b3",

    fontSize:
        "7px",

    fontWeight:
        "800",

    letterSpacing:
        "1px"
};


//--------------------------------------------------------
// Legend
//--------------------------------------------------------

const legendStyle =
{
    display: "flex",

    alignItems: "center",
    justifyContent: "center",

    gap: "13px",

    marginTop: "9px"
};


const legendItemStyle =
{
    display: "flex",

    alignItems: "center",

    gap: "4px",

    color:
        "#929ba7",

    fontSize:
        "8px",

    fontWeight:
        "600"
};


const legendColourStyle =
{
    width: "7px",
    height: "7px",

    borderRadius: "50%",

    boxShadow:
        "0 1px 3px rgba(0,0,0,0.12)"
};